import type { CartLineItem, Product, ProductVariant } from '~/types'
import { normalizeCartItems } from '~/utils/cartNormalize'
import {
  buildCustomFieldsKey,
  computeCustomFieldsPriceDelta,
  computeFieldPriceDelta,
  resolveAssignmentPricing,
  type CustomFieldValueInput,
} from '~/utils/complexProductFields'
import { productIsPackage, buildHybridCartLineKey } from '~/utils/productPackage'
import {
  appendVariationSelectionsKey,
  attributeSelectionSnapshots,
  sumAttributeExtras,
} from '~/utils/productVariants'
import { linePriceUnit, normalizePriceUnit, normalizeServiceQuantity, productHasPriceUnit } from '~/utils/shopServices'

export function useCart() {
  const cartStore = useCartStore()
  const wishlistStore = useWishlistStore()
  const toast = useToast()
  const shopPath = useShopPath()
  const { get, post, put, del } = useApi()
  const { stockManagementEnabled, shopServicesEnabled } = useSiteSettings()
  const { flyToCart } = useFlyToCart()

  const items = computed(() => cartStore.items)
  const itemCount = computed(() => cartStore.itemCount)
  const subtotal = computed(() => cartStore.subtotal)
  const isOpen = computed({
    get: () => cartStore.isDrawerOpen,
    set: (value: boolean) => cartStore.setDrawerOpen(value),
  })

  async function pruneUnavailableItems(options?: { silent?: boolean }) {
    try {
      const response = await get<{ productIds: number[] }>('/products/reserved-ids')
      const reservedIds = response.data?.productIds ?? []
      if (!reservedIds.length) return

      const removedFromCart = cartStore.pruneReservedItems(reservedIds)
      wishlistStore.removeByProductIds(reservedIds)

      if (!options?.silent && removedFromCart > 0) {
        toast.add({
          title: 'Produse indisponibile eliminate',
          description: 'Unele produse au fost scoase din coș pentru că nu mai sunt disponibile.',
          color: 'warning',
        })
      }
    } catch {
      // ignore network errors during background prune
    }
  }

  async function openDrawer() {
    await pruneUnavailableItems({ silent: true })
    if (useAuthStore().isAuthenticated) {
      await syncCart()
    }
    cartStore.setDrawerOpen(true)
  }

  function closeDrawer() {
    cartStore.setDrawerOpen(false)
  }

  async function toggleDrawer() {
    if (!cartStore.isDrawerOpen) {
      await openDrawer()
      return
    }
    cartStore.setDrawerOpen(false)
  }

  async function addToCart(
    product: Product,
    quantity = 1,
    variant?: ProductVariant,
    options?: {
      openDrawer?: boolean
      toast?: boolean
      flyFrom?: HTMLElement | null
      imageUrl?: string | null
      customFields?: CustomFieldValueInput[]
      packageItemProductIds?: number[]
      variationSelections?: Record<string, string>
      packageUnitPrice?: number
      packageSnapshots?: Array<{
        fieldId: number
        name: string
        type: string
        value: unknown
        priceDelta: number
        itemRole?: 'product' | 'equipment' | 'service' | 'extra'
      }>
    },
  ) {
    const isPackage = productIsPackage(product)
    const skipStock = isPackage
      || shopServicesEnabled.value
      || !stockManagementEnabled.value
      || Number(product.generatorId) > 0
    const availableStock = Math.max(0, Number(variant?.stock ?? product.stock ?? 0))
    if (!skipStock && availableStock <= 0) {
      toast.add({
        title: 'Stoc epuizat',
        description: product.name,
        color: 'warning',
      })
      return
    }

    if (isPackage && !options?.packageItemProductIds?.length) {
      toast.add({
        title: 'Alege produsele din pachet',
        description: product.name,
        color: 'warning',
      })
      return
    }

    const customFields = options?.customFields || []
    const packageItemProductIds = options?.packageItemProductIds
    const variationSelections = options?.variationSelections
    const customFieldsOnlyKey = buildCustomFieldsKey(customFields)
    const customFieldsKey = appendVariationSelectionsKey(
      buildHybridCartLineKey(customFieldsOnlyKey, packageItemProductIds),
      variationSelections,
    )

    const existing = cartStore.items.find(
      (item) =>
        item.productId === product.id
        && item.variantId === variant?.id
        && (item.customFieldsKey || '') === customFieldsKey,
    )
    const alreadyInCart = existing?.quantity ?? 0
    const remaining = !skipStock
      ? availableStock - alreadyInCart
      : Math.max(1, quantity)

    if (!skipStock && remaining <= 0) {
      toast.add({
        title: 'Cantitate maximă în coș',
        description: `Ai deja maximul disponibil (${availableStock}) pentru ${product.name}.`,
        color: 'warning',
      })
      if (options?.openDrawer !== false) {
        openDrawer()
      }
      return
    }

    const minQty = productHasPriceUnit(product) ? 0.01 : 1
    const requested = productHasPriceUnit(product)
      ? (normalizeServiceQuantity(quantity) ?? minQty)
      : Math.max(minQty, quantity)
    const qtyToAdd = !skipStock
      ? Math.min(requested, remaining)
      : requested

    const attributeDelta = variant
      ? 0
      : sumAttributeExtras(product.variationAttributes, variationSelections)
    const basePrice = isPackage
      ? Number(options?.packageUnitPrice ?? 0)
      : Number(variant?.salePrice ?? variant?.price ?? product.salePrice ?? product.price) + attributeDelta
    const valuesMap = Object.fromEntries(customFields.map((entry) => [entry.fieldId, entry.value]))
    const priceDelta = computeCustomFieldsPriceDelta(
      product.complexFieldAssignments || [],
      valuesMap,
      basePrice,
    )
    const unitPrice = basePrice + priceDelta
    const referenceForPercent = basePrice + computeCustomFieldsPriceDelta(
      (product.complexFieldAssignments || []).filter((row) => {
        const mode = resolveAssignmentPricing(row).pricingMode
        return mode !== 'per_unit_percent'
      }),
      valuesMap,
      0,
    )

    const complexSnapshots = customFields.map((entry) => {
      const assignment = (product.complexFieldAssignments || []).find((row) => row.fieldId === entry.fieldId)
      const field = assignment?.field
      const pricing = assignment
        ? resolveAssignmentPricing(assignment)
        : {
            pricingMode: 'none' as const,
            pricingAmount: 0,
            pricingMinUnits: 0,
            pricingExtraAmount: 0,
          }
      return {
        fieldId: entry.fieldId,
        name: field?.name || `Câmp #${entry.fieldId}`,
        type: field?.fieldType || 'text',
        value: entry.value,
        priceDelta: field
          ? computeFieldPriceDelta(
              field.fieldType,
              pricing.pricingMode,
              pricing.pricingAmount,
              entry.value,
              {
                pricingMinUnits: pricing.pricingMinUnits,
                pricingExtraAmount: pricing.pricingExtraAmount,
                basePrice: pricing.pricingMode === 'per_unit_percent' ? referenceForPercent : undefined,
              },
            )
          : 0,
        hideFromCustomer: field?.hideFromCustomer === true,
      }
    })

    const lineItem: CartLineItem = {
      productId: product.id,
      variantId: isPackage ? undefined : variant?.id,
      quantity: qtyToAdd,
      product,
      variant: isPackage ? undefined : variant,
      unitPrice,
      totalPrice: unitPrice * qtyToAdd,
      customFields: customFields.length ? customFields : undefined,
      customFieldsKey,
      customFieldsSnapshot: [
        ...(isPackage ? (options?.packageSnapshots || []) : []),
        ...attributeSelectionSnapshots(product.variationAttributes, variationSelections),
        ...complexSnapshots,
      ],
      packageItemProductIds,
      variationSelections,
      priceUnit: normalizePriceUnit(product.priceUnit),
    }

    cartStore.addItem(lineItem)

    const flyMs = flyToCart({
      from: options?.flyFrom,
      imageUrl: options?.imageUrl
        || product.images?.find((img) => img.isPrimary && !img.is360)?.url
        || product.images?.[0]?.url,
    })

    if (useAuthStore().isAuthenticated) {
      try {
        await post('/orders/cart', {
          productId: product.id,
          variantId: isPackage ? undefined : variant?.id,
          quantity: qtyToAdd,
          customFields: customFields.length ? customFields : undefined,
          customFieldsKey,
          packageItemProductIds,
          variationSelections,
        })
      } catch (err: unknown) {
        const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        if (message) {
          toast.add({ title: message, color: 'error' })
        }
      }
    }

    if (options?.toast !== false) {
      toast.add({
        title: qtyToAdd < quantity ? 'Cantitate ajustată la stoc' : 'Produs adăugat în coș',
        description: product.name,
        color: 'success',
        actions: [{ label: 'Vezi coșul', onClick: () => navigateTo(shopPath('/cos')) }],
      })
    }
    if (options?.openDrawer !== false) {
      if (flyMs > 0) {
        window.setTimeout(() => {
          void openDrawer()
        }, flyMs)
      } else {
        void openDrawer()
      }
    }

    useGtmEcommerce().trackAddToCart(product, qtyToAdd, variant)
    useMetaEcommerce().trackAddToCart(product, qtyToAdd, variant)
  }

  async function updateQuantity(
    productId: number,
    quantity: number,
    variantId?: number,
    customFieldsKey?: string,
  ) {
    if (quantity <= 0) {
      await removeFromCart(productId, variantId, customFieldsKey)
      return
    }

    const existing = cartStore.items.find(
      (item) =>
        item.productId === productId
        && item.variantId === variantId
        && (item.customFieldsKey || '') === (customFieldsKey || ''),
    )
    const isPackage = productIsPackage(existing?.product)
      || Boolean(existing?.packageItemProductIds?.length)
    const skipStock = isPackage
      || shopServicesEnabled.value
      || !stockManagementEnabled.value
      || Number(existing?.product?.generatorId) > 0
    const maxStock = Math.max(0, Number(existing?.variant?.stock ?? existing?.product?.stock ?? 0))
    const measured = Boolean(linePriceUnit(existing || {}))
    const requested = measured
      ? (normalizeServiceQuantity(quantity) ?? quantity)
      : quantity
    const nextQty = !skipStock && maxStock > 0
      ? Math.min(requested, maxStock)
      : requested

    if (!skipStock && maxStock > 0 && requested > maxStock) {
      toast.add({
        title: 'Stoc insuficient',
        description: `Maxim ${maxStock} bucăți disponibile.`,
        color: 'warning',
      })
    }

    cartStore.updateQuantity(productId, nextQty, variantId, customFieldsKey)

    if (useAuthStore().isAuthenticated) {
      try {
        await put(`/orders/cart/${productId}`, {
          variantId,
          quantity: nextQty,
          customFieldsKey: customFieldsKey || '',
        })
      } catch {
        // ignore
      }
    }
  }

  async function removeFromCart(productId: number, variantId?: number, customFieldsKey?: string) {
    cartStore.removeItem(productId, variantId, customFieldsKey)

    if (useAuthStore().isAuthenticated) {
      try {
        await del(`/orders/cart/${productId}`, {
          params: {
            variantId,
            customFieldsKey: customFieldsKey || '',
          },
        })
      } catch {
        // ignore
      }
    }
  }

  async function syncCart() {
    if (!useAuthStore().isAuthenticated) return
    try {
      const { get, post } = useApi()
      const response = await get<{ items: CartLineItem[] }>('/orders/cart')
      if (!response.success) return

      const serverItems = response.data?.items ?? []
      if (serverItems.length > 0) {
        // Redis returns productName/image; UI needs nested product — normalize + keep local product when present
        cartStore.setItems(normalizeCartItems(serverItems, cartStore.items))
        return
      }

      if (cartStore.items.length > 0) {
        for (const item of cartStore.items) {
          await post('/orders/cart', {
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity,
            customFields: item.customFields,
            customFieldsKey: item.customFieldsKey,
            packageItemProductIds: item.packageItemProductIds,
            variationSelections: item.variationSelections,
          })
        }
      }
    } catch {
      // păstrăm coșul local
    }
  }

  function clearCart() {
    cartStore.clear()
  }

  return {
    items,
    itemCount,
    subtotal,
    isOpen,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    addToCart,
    updateQuantity,
    removeFromCart,
    syncCart,
    clearCart,
    pruneUnavailableItems,
  }
}
